import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {BudgetingType} from '../../../../BudgetingType';
import {
    UpsertUnitTypeScheduleValuesInputRequireBudgetYear, useGetForecastLocksLazyQuery,
    useGetUtsvOperationalMetricsForPropertyYearLazyQuery,
    useUpsertMultipleUnitTypeScheduleValuesMutation, VersionType
} from '../../../../__generated__/generated_types';
import {getBudgetYearForProperty, useProperties} from '../../../../contexts/properties/PropertiesContext';

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as valuesRewriteModalCSS from "../occupancy-rates/valuesRewriteModal.module.scss";
import * as css from "./styles/combined-metrics.module.scss";

import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import {Help, Sync} from '@material-ui/icons';
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {Message} from '@zendeskgarden/react-forms';
import {Body, Close, Header, Modal, TooltipModal} from '@zendeskgarden/react-modals';
import {UnorderedList} from '@zendeskgarden/react-typography';
import "native-injects";
import {ImMagicWand} from 'react-icons/im';
import RevenueWizard from '../../../../components/revenue-wizard/RevenueWizard';
import VZSaveStatus from '../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {AuthContext} from "../../../../contexts/AuthContext";
import {useVersions} from '../../../../contexts/versions/VersionsContext';
import {hideRevenueWizard} from '../../../../global/feature_checkers';
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import {IBatchUpsertInput} from '../hooks/useUTSVOperationalMetrics';
import {LeaseExpirationsRewriteView} from '../lease-expiration/LeaseExpirationsRewriteView';
import {LeaseExpirationCurveModal} from '../lease-expiration/curve/LeaseExpirationCurveModal';
import {OccupancyRatesRewriteView} from '../occupancy-rates/OccupancyRatesRewriteView';
import {getVersionLabel, IUTSVVersion, parseUTSVQueryResponse} from '../utils/operational-metric-helpers';
import CombinedMetricsTable from './components/CombinedMetricsTable';
import {toast} from 'react-toastify';


export default function OccupancyManager(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType};
    const {currentProperty} = useProperties();
    const auth = useContext(AuthContext);
    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const currentVersionYear = versionType === VersionType.Budget ? budgetYear : budgetYear - 1;

    const {versions} = useVersions();
    const rfcstVersionId = versions?.valueVersions.find(x => x.year == budgetYear - 1 && x.type == VersionType.Reforecast)?.id;
    const bdgtVersionId = versions?.valueVersions.find(x => x.year == budgetYear && x.type == VersionType.Budget)?.id;
    const versionIdToSave = versionType === VersionType.Reforecast ? rfcstVersionId : bdgtVersionId;
    const isRevWizHidden = hideRevenueWizard(auth.user);

    const syncButtonRef = useRef<HTMLButtonElement>(null);
    const triggerRefreshAfterNextSave = useRef<boolean>(false);

    const [wizardOpen, setWizardOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [collectedChanges, setCollectedChanges] = useState<IBatchUpsertInput[]>([]);
    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [syncWithPMSOpen, setSyncWithPMSOpen] = useState(false);
    const [showRewriteModal, setShowRewriteModal] = useState<"expirations" | "occupancy" | null>(null);
    const [showExpirationCurveModal, setShowExpirationCurveModal] = useState(false);

    const [getForecastLocks, {data: forecastLocks, loading: locksLoading}] = useGetForecastLocksLazyQuery({fetchPolicy: "no-cache"});
    const [getMetricData, {data: metricData, loading: metricDataLoading, refetch: fetchMetricData}] = useGetUtsvOperationalMetricsForPropertyYearLazyQuery({fetchPolicy: "no-cache"});
    const [upsertMultipleUnitTypeScheduleValues, {loading: savingChanges}] = useUpsertMultipleUnitTypeScheduleValuesMutation({notifyOnNetworkStatusChange: true});

    const parsedMetricData = useMemo<IUTSVVersion[]>(() => {
        if (!metricData?.originalRevenueUTSVOperationalMetricsForPropertyYear) {
            return [];
        }
        return parseUTSVQueryResponse(metricData.originalRevenueUTSVOperationalMetricsForPropertyYear);
    }, [metricData]);

    const unitTypes = useMemo<{id: string, type: string}[]>(() => {
        let unitTypeReturn: {id: string, type: string}[] = [];
        let firstVersionAvailable = parsedMetricData[0];
        if (firstVersionAvailable) {
            unitTypeReturn = firstVersionAvailable.unitTypes.map(x => ({id: x.unitTypeId, type: x.unitTypeName}));
        }
        return unitTypeReturn;
    }, [parsedMetricData]);

    useEffect(
        () => {
            const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
            if (updateWorkflowTab != false) {
                setSelectedTab(updateWorkflowTab as string);
            }
            fetchMetricData?.();
        },
        [type]
    );

    useEffect(() => {
        if (currentProperty) {
            getForecastLocks({
                variables: {
                    propertyId: currentProperty.id,
                    budgetYear: budgetYear
                }
            });
            getMetricData({
                variables: {
                    propertyId: currentProperty.id,
                    budgetYear: budgetYear,
                }
            });
        }
    }, [currentProperty]);

    useEffect(() => {
        if(!currentProperty || savingChanges || collectedChanges.length === 0) {
            return;
        }

        const mergedChanges: IBatchUpsertInput[] = [];

        for (const change of collectedChanges) {
            const {fieldName, unitTypeName, monthIndex, value} = change;
            const existingPendingChange = mergedChanges.find(x => x.fieldName === fieldName && x.unitTypeName === unitTypeName && x.monthIndex === monthIndex);
            if (existingPendingChange) {
                existingPendingChange.value = value;
            } else {
                mergedChanges.push(change);
            }
        }

        const batchArgs = changesToBatchArgs(mergedChanges);
        setCollectedChanges([]);

        upsertMultipleUnitTypeScheduleValues({
            variables: {
                upsertArgs: batchArgs,
            },
        }).then(res => {
            if(res.data?.upsertMultipleUnitTypeScheduleValues) {
                setLastSaved(new Date());
                if (triggerRefreshAfterNextSave.current === true) {
                    fetchMetricData?.();
                }
                triggerRefreshAfterNextSave.current = false;
                return;
            }
            toast.error("Failed to save metrics, refresh and try again");
        }).catch(() => {
            toast.error("Failed to save metrics, refresh and try again");
        });
    }, [savingChanges, collectedChanges]);

    const tableLocked = useMemo<boolean>(() => {
        if (locksLoading) {
            return true;
        }
        const rfcstOpen = forecastLocks?.forecastLocks?.property.reforecastLocked === false;
        const bdgtOpen = forecastLocks?.forecastLocks?.property.budgetLocked === false;
        return versionType === VersionType.Reforecast ? !rfcstOpen : !bdgtOpen;
    }, [locksLoading, forecastLocks]);

    function changesToBatchArgs(changes: IBatchUpsertInput[]): UpsertUnitTypeScheduleValuesInputRequireBudgetYear[] {
        const changesByMetric = changes.groupBy("fieldName");
        const batchArgs: UpsertUnitTypeScheduleValuesInputRequireBudgetYear[] = [];

        if (!currentProperty || !versionIdToSave) {
            return batchArgs;
        }

        for (const [metric, values] of Object.entries(changesByMetric)) {
            batchArgs.push({
                fieldName: metric,
                propertyId: currentProperty.id,
                budgetYear,
                versionId: versionIdToSave,
                values: values.map(x => ({
                    unitTypeName: x.unitTypeName,
                    monthIndex: x.monthIndex,
                    value: x.value,
                })),
            });
        }
        return batchArgs;
    }

    function handleChange(changes: IBatchUpsertInput[], triggerRefresh?: boolean): void {
        setCollectedChanges([...collectedChanges, ...changes]);
        triggerRefreshAfterNextSave.current = triggerRefresh === true;
    }

    if (!currentProperty) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen} ${css.combinedMetrics}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
                            <WorkflowPageLabel label="Occupancy Manager" />
                            <IconButton size="small" onClick={() => setHelpOpen(true)}><Help /></IconButton>
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingChanges} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>

                <div className={`${workflowCSS.bodyContainer} ${css.tableWrapper}`}>
                    {currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <CombinedMetricsTable
                                    property={currentProperty}
                                    versionType={versionType}
                                    year={currentVersionYear}
                                    tableLocked={locksLoading || tableLocked}
                                    isLoading={metricDataLoading || locksLoading || savingChanges}
                                    unitVersionData={parsedMetricData}
                                    onChange={handleChange}
                                    controlsSlot={
                                        <>
                                            {
                                                // since revenue wizard sets values for both rfcst and bdgt seasons, make sure it's hidden if either season is locked
                                                forecastLocks?.forecastLocks?.property.reforecastLocked === false &&
                                                forecastLocks?.forecastLocks?.property.budgetLocked === false &&
                                                !isRevWizHidden &&
                                                    <Button isPrimary size="small" onClick={() => setWizardOpen(true)}>
                                                        <Button.StartIcon><ImMagicWand /></Button.StartIcon>
                                                        Revenue Wizard
                                                    </Button>
                                            }
                                            {
                                                !tableLocked &&
                                                type == BudgetingType.BUDGET &&
                                                    <Button ref={syncButtonRef} isBasic size="small" onClick={() => setShowExpirationCurveModal(true)}>
                                                        Use Expiration Curve
                                                    </Button>
                                            }
                                            {
                                                !tableLocked &&
                                                !currentProperty?.isAcquisitionProperty &&
                                                    <Button ref={syncButtonRef} isBasic size="small" onClick={() => setSyncWithPMSOpen(true)}>
                                                        <Button.StartIcon><Sync /></Button.StartIcon>
                                                        Sync with PMS
                                                    </Button>
                                            }
                                        </>
                                    }
                                />
                            </div>
                        </>
                    }
                </div>
            </div>

            {
                metricData &&
                !currentProperty?.isAcquisitionProperty &&
                !forecastLocks?.forecastLocks?.property.reforecastLocked &&
                !forecastLocks?.forecastLocks?.property.budgetLocked &&
                !isRevWizHidden &&
                    <RevenueWizard
                        property={currentProperty}
                        budgetYear={budgetYear}
                        isOpen={wizardOpen}
                        metricData={metricData.originalRevenueUTSVOperationalMetricsForPropertyYear}
                        onClose={dataSaved => {
                            setWizardOpen(false);
                            if (dataSaved && fetchMetricData) {
                                fetchMetricData();
                            }
                        }}
                    />
            }
            {helpOpen &&
                <Modal onClose={() => setHelpOpen(false)}>
                    <Header>About the Occupancy Manager</Header>
                    <Body>
                        <h5>The Occupancy Manager gives you one place to manage all of the metrics driving your occupancy.</h5>
                        <p>Here you can:</p>
                        <UnorderedList className={css.helpList}>
                            <UnorderedList.Item>View and make changes to expirations, renewals, move outs, and occupancy.</UnorderedList.Item>
                            <UnorderedList.Item>Manage renewals, move outs, and occupancy as either rates or counts.*</UnorderedList.Item>
                            <UnorderedList.Item>Make changes to renewals and have the system automatically update move outs to total 100%.</UnorderedList.Item>
                            <UnorderedList.Item>View calculated move ins in real time.</UnorderedList.Item>
                        </UnorderedList>
                        <Message>*Property-level changes can only be made when working with rates.</Message>
                    </Body>
                    <Close aria-label="Close help modal" />
                </Modal>
            }

            {syncWithPMSOpen && syncButtonRef.current &&
                <TooltipModal
                    referenceElement={syncButtonRef.current}
                    onClose={() => setSyncWithPMSOpen(false)}
                    placement="start-top"
                    className={css.settingsMenu}
                >
                    <TooltipModal.Body className={css.settingsBody}>
                        <div className={css.itemGroup}>
                            <h5 className={css.itemGroupLabel}>Select Metric</h5>
                            <Button isPrimary onClick={() => setShowRewriteModal("expirations")}>
                                <Button.StartIcon><Sync /></Button.StartIcon>
                                Sync Expirations
                            </Button>
                            <Button isPrimary onClick={() => setShowRewriteModal("occupancy")}>
                                <Button.StartIcon><Sync /></Button.StartIcon>
                                Sync Occupancy
                            </Button>
                        </div>
                    </TooltipModal.Body>
                    <TooltipModal.Close aria-label="Close pms sync picker" />
                </TooltipModal>
            }

            {showRewriteModal !== null && currentProperty && type &&
                <Modal
                    onClose={() => setShowRewriteModal(null)}
                    isLarge
                    className={valuesRewriteModalCSS.valuesModal}>
                    <Header>
                        Sync PMS values to your assumptions for {getVersionLabel(versionType, currentVersionYear, currentProperty)}
                    </Header>
                    <Body className={valuesRewriteModalCSS.valuesModalBody}>
                        {showRewriteModal === "occupancy" ?
                            <OccupancyRatesRewriteView
                                property={currentProperty}
                                budgetYear={budgetYear}
                                versionType={versionType}
                                unitTypes={unitTypes}
                                valuesStartFromIndex={type == BudgetingType.BUDGET ? 0 : currentProperty.reforecastStartMonthIndex}
                                onCancel={() => setShowRewriteModal(null)}
                                onApply={() => {
                                    setShowRewriteModal(null);
                                    fetchMetricData?.();
                                }}
                            />
                            :
                            <LeaseExpirationsRewriteView
                                propertyId={currentProperty.id}
                                budgetYear={budgetYear}
                                versionType={type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast}
                                valuesStartFromIndex={type == BudgetingType.BUDGET ? 0 : currentProperty.reforecastStartMonthIndex}
                                onCancel={() => setShowRewriteModal(null)}
                                onApply={() => {
                                    setShowRewriteModal(null);
                                    fetchMetricData?.();
                                }}
                            />
                        }
                    </Body>
                    <Close aria-label="Close pms sync modal" />
                </Modal>
            }

            {showExpirationCurveModal && currentProperty && type == BudgetingType.BUDGET &&
                <LeaseExpirationCurveModal
                    property={currentProperty}
                    budgetYear={budgetYear}
                    handleCancel={() => setShowExpirationCurveModal(false)}
                    handleConfirm={() => {
                        setShowExpirationCurveModal(false);
                        fetchMetricData?.();
                    }}
                />
            }
        </ThemeProvider>
    );
}
